import React, { useState, useEffect } from 'react';
import {
  Card,
  Input,
  Button,
  Typography,
  Checkbox,
  message
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { Shield, Lock, Activity } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DisplayCards from '../ui/display-cards';
import { useTheme } from '../ParticleBackground';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import useResponsive from '../../hooks/useResponsive';
import './AdminLogin.css';

const { Title, Text } = Typography;

const AdminLogin = () => {
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimeRemaining, setBlockTimeRemaining] = useState(0);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });

  const { theme } = useTheme();
  // Switch to AdminAuthContext
  const { adminLogin, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Security: Block login attempts after multiple failures
  useEffect(() => {
    const attemptData = localStorage.getItem('admin_login_attempts');
    if (attemptData) {
      try {
        const { count, lastAttempt } = JSON.parse(attemptData);
        const timeSinceLastAttempt = Date.now() - lastAttempt;

        // Block for 15 minutes after 3 failed attempts
        if (count >= 3 && timeSinceLastAttempt < 900000) {
          setIsBlocked(true);
          setLoginAttempts(count);
          setBlockTimeRemaining(Math.ceil((900000 - timeSinceLastAttempt) / 60000));

          // Start countdown timer
          const timer = setInterval(() => {
            const newTimeRemaining = Math.ceil((900000 - (Date.now() - lastAttempt)) / 60000);
            if (newTimeRemaining <= 0) {
              setIsBlocked(false);
              setLoginAttempts(0);
              localStorage.removeItem('admin_login_attempts');
              clearInterval(timer);
            } else {
              setBlockTimeRemaining(newTimeRemaining);
            }
          }, 60000);

          return () => clearInterval(timer);
        } else if (timeSinceLastAttempt >= 900000) {
          // Reset after 15 minutes
          localStorage.removeItem('admin_login_attempts');
          setLoginAttempts(0);
        } else {
          setLoginAttempts(count);
        }
      } catch (error) {
        console.error('Error parsing login attempts:', error);
        localStorage.removeItem('admin_login_attempts');
      }
    }
  }, []);

  // Admin-themed cards for the display
  const adminCards = [
    {
      icon: <Shield style={{ width: '16px', height: '16px', color: '#000000' }} />,
      title: "Secure Access",
      description: "Encrypted admin portal",
      date: "256-bit SSL",
      className: "card-1",
      style: {
        gridArea: 'stack',
        transform: 'translateY(0px) skewY(-8deg)',
        transition: 'all 0.7s ease',
        filter: 'grayscale(100%)',
        position: 'relative',
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
    {
      icon: <Lock style={{ width: '16px', height: '16px', color: '#000000' }} />,
      title: "Data Privacy",
      description: "HIPAA Compliant Controls",
      date: "Strict Auditing",
      className: "card-2",
      style: {
        gridArea: 'stack',
        transform: 'translateX(64px) translateY(40px) skewY(-8deg)',
        transition: 'all 0.7s ease',
        filter: 'grayscale(100%)',
        position: 'relative',
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
    {
      icon: <Activity style={{ width: '16px', height: '16px', color: '#000000' }} />,
      title: "System Status",
      description: "Real-time monitoring",
      date: "99.9% Uptime",
      className: "card-3",
      style: {
        gridArea: 'stack',
        transform: 'translateX(128px) translateY(80px) skewY(-8deg)',
        transition: 'all 0.7s ease',
        position: 'relative',
        backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        borderColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
      }
    },
  ];

  // Theme-aware styles for black/white design
  const getAdminThemeStyles = () => {
    if (theme === 'dark') {
      return {
        pageBackground: '#000000',
        gridColor: 'rgba(255, 255, 255, 0.1)',
        leftPanel: { background: 'transparent' },
        rightPanel: { background: 'transparent' },
        cardBackground: 'rgba(255, 255, 255, 0.05)',
        cardBorder: 'rgba(255, 255, 255, 0.1)',
        cardBackdrop: 'blur(20px)',
        titleColor: '#ffffff',
        subtitleColor: '#a0a0a0',
        labelColor: '#e0e0e0',
        inputBackground: 'rgba(255, 255, 255, 0.05)',
        inputBorder: 'rgba(255, 255, 255, 0.2)',
        inputFocusBorder: '#ffffff',
        inputText: '#ffffff',
        inputPlaceholder: '#a0a0a0',
        primaryButton: '#ffffff',
        primaryButtonText: '#000000',
        primaryButtonHover: '#f0f0f0',
        secondaryButton: 'transparent',
        secondaryButtonBorder: 'rgba(255, 255, 255, 0.3)',
        secondaryButtonText: '#ffffff',
      };
    } else {
      return {
        pageBackground: '#ffffff',
        gridColor: 'rgba(0, 0, 0, 0.1)',
        leftPanel: { background: 'transparent' },
        rightPanel: { background: 'transparent' },
        cardBackground: 'rgba(0, 0, 0, 0.02)',
        cardBorder: 'rgba(0, 0, 0, 0.1)',
        cardBackdrop: 'blur(20px)',
        titleColor: '#000000',
        subtitleColor: '#666666',
        labelColor: '#333333',
        inputBackground: 'rgba(0, 0, 0, 0.02)',
        inputBorder: 'rgba(0, 0, 0, 0.2)',
        inputFocusBorder: '#000000',
        inputText: '#000000',
        inputPlaceholder: '#666666',
        primaryButton: '#000000',
        primaryButtonText: '#ffffff',
        primaryButtonHover: '#333333',
        secondaryButton: 'transparent',
        secondaryButtonBorder: 'rgba(0, 0, 0, 0.3)',
        secondaryButtonText: '#000000',
      };
    }
  };

  const adminThemeStyles = getAdminThemeStyles();

  // --- STYLES ---
  const styles = {
    pageContainer: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
      background: adminThemeStyles.pageBackground,
      position: 'relative',
    },
    container: {
      flex: 1,
      display: 'flex',
      overflow: 'hidden',
      paddingTop: '64px',
      position: 'relative',
      zIndex: 2
    },
    leftPanel: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '60px 40px',
      position: 'relative',
      minHeight: 'calc(100vh - 64px)',
      ...adminThemeStyles.leftPanel
    },
    rightPanel: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px',
      minHeight: 'calc(100vh - 64px)',
      ...adminThemeStyles.rightPanel
    },
    signInCard: {
      width: '100%',
      maxWidth: '420px',
      borderRadius: '24px',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)',
      background: adminThemeStyles.cardBackground,
      backdropFilter: adminThemeStyles.cardBackdrop,
      border: `1px solid ${adminThemeStyles.cardBorder}`,
      overflow: 'hidden',
      transform: mounted ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
      opacity: mounted ? 1 : 0,
      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
      minHeight: 480,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    header: {
      textAlign: 'center',
      marginBottom: '32px',
      padding: '40px 40px 0'
    },
    logoContainer: {
      width: '64px',
      height: '64px',
      background: theme === 'dark' ? '#ffffff' : '#000000',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 20px',
      boxShadow: `0 8px 32px ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
      animation: mounted ? 'float 3s ease-in-out infinite' : 'none'
    },
    brandName: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: adminThemeStyles.titleColor,
      marginBottom: '8px'
    },
    subtitle: {
      color: adminThemeStyles.subtitleColor,
      fontSize: '15px'
    },
    formContainer: {
      padding: '0 40px 28px'
    },
    inputGroup: {
      marginBottom: '20px',
      transform: mounted ? 'translateX(0)' : 'translateX(30px)',
      opacity: mounted ? 1 : 0,
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: adminThemeStyles.labelColor
    },
    input: {
      borderRadius: '12px',
      height: '48px',
      fontSize: '15px',
      border: `2px solid ${adminThemeStyles.inputBorder}`,
      background: adminThemeStyles.inputBackground,
      color: adminThemeStyles.inputText,
      transition: 'all 0.3s ease'
    },
    submitButton: {
      width: '100%',
      height: '48px',
      fontSize: '16px',
      fontWeight: '600',
      borderRadius: '12px',
      border: 'none',
      background: adminThemeStyles.primaryButton,
      color: adminThemeStyles.primaryButtonText,
      boxShadow: `0 4px 20px ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
      transition: 'all 0.3s ease',
      marginBottom: '18px'
    }
  };

  // Add keyframe animations
  useEffect(() => {
    if (!document.getElementById('admin-login-enhanced-styles')) {
      const style = document.createElement('style');
      style.id = 'admin-login-enhanced-styles';
      style.textContent = `
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .admin-login-container {
            flex-direction: column !important;
          }
          .admin-left-panel {
            min-height: 50vh !important;
            padding: 40px 20px !important;
          }
          .admin-right-panel {
            flex: 1 !important;
            padding: 20px !important;
            min-height: 50vh !important;
          }
        }
        .input-group-1 { transition-delay: 0.1s; }
        .input-group-2 { transition-delay: 0.2s; }
        .signin-input:focus {
          border-color: ${adminThemeStyles.inputFocusBorder} !important;
          box-shadow: 0 0 0 3px ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'} !important;
        }
        .signin-submit-button:hover {
          transform: translateY(-2px) !important;
          background: ${adminThemeStyles.primaryButtonHover} !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById('admin-login-enhanced-styles');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [theme, adminThemeStyles]);

  // Generate browser fingerprint for additional security
  const generateBrowserFingerprint = async () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('EkahHealth Admin Security', 2, 2);

    return {
      canvas: canvas.toDataURL(),
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screen: {
        width: window.screen.width, // Fix: use window.screen
        height: window.screen.height, // Fix: use window.screen
        colorDepth: window.screen.colorDepth // Fix: use window.screen
      }
    };
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogin = async () => {
    if (isBlocked) {
      setError(`Too many failed attempts. Please wait ${blockTimeRemaining} minutes.`);
      return;
    }

    if (!formData.username || !formData.password) {
      message.error('Please enter both username and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use AdminAuthContext credentials structure
      // Note: AdminAuthContext doesn't take rememberMe or fingerprint yet, but we'll generate it anyway for future use
      await generateBrowserFingerprint();

      const result = await adminLogin(formData.username.trim(), formData.password);

      if (result) {
        localStorage.removeItem('admin_login_attempts');
        setLoginAttempts(0);
        message.success('Welcome back, Admin!');
        navigate('/admin'); // Explicit navigation
      }
    } catch (error) {
      console.error('Login error:', error);

      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);

      localStorage.setItem('admin_login_attempts', JSON.stringify({
        count: newAttempts,
        lastAttempt: Date.now()
      }));

      if (newAttempts >= 3) {
        setIsBlocked(true);
        setBlockTimeRemaining(15);
        setError('Too many failed attempts. Account blocked for 15 minutes for security.');
      } else {
        setError(error.message || 'Invalid credentials. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: theme === 'dark' ? '#000000' : '#ffffff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: `3px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'}`,
            borderTopColor: theme === 'dark' ? '#ffffff' : '#000000',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <Text style={{
            color: theme === 'dark' ? '#ffffff' : '#000000',
            fontSize: '16px'
          }}>
            Initializing secure admin session...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.container} className="admin-login-container">
        {/* Left Panel - Display Cards */}
        <div style={{
          ...styles.leftPanel,
          display: isMobile ? 'none' : 'flex'
        }} className="admin-left-panel">
          <div style={{
            marginBottom: '60px',
            textAlign: 'center',
            zIndex: 2,
            position: 'absolute',
            top: '10%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%'
          }}>
            <Title level={1} style={{
              color: adminThemeStyles.titleColor,
              fontSize: '42px',
              fontWeight: 'bold',
              marginBottom: '16px'
            }}>
              EkahHealth Admin
            </Title>
            <Text style={{
              color: adminThemeStyles.subtitleColor,
              fontSize: '18px'
            }}>
              Secure Portal Access
            </Text>
          </div>
          <div style={{
            zIndex: 2,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            top: '30%',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <DisplayCards cards={adminCards} />
          </div>
        </div>
        {/* Right Panel - Login Form */}
        <div style={styles.rightPanel} className="admin-right-panel">
          <Card style={styles.signInCard} bordered={false}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.logoContainer}>
                <SafetyCertificateOutlined style={{
                  fontSize: '28px',
                  color: theme === 'dark' ? '#000000' : '#ffffff'
                }} />
              </div>
              <div style={styles.brandName}>Admin Portal</div>
              <Text style={styles.subtitle}>
                Please enter your admin credentials to access the dashboard.
              </Text>
            </div>
            <div style={styles.formContainer}>
              {/* Error Display */}
              {(error || isBlocked) && (
                <div style={{
                  padding: '12px',
                  marginBottom: '18px',
                  borderRadius: '8px',
                  backgroundColor: theme === 'dark' ? 'rgba(254, 226, 226, 0.1)' : 'rgba(254, 226, 226, 0.5)',
                  borderLeft: '4px solid #ef4444',
                  color: theme === 'dark' ? '#fca5a5' : '#b91c1c'
                }}>
                  {isBlocked ? `Account blocked for ${blockTimeRemaining}m` : error}
                </div>
              )}

              {/* Username Field */}
              <div style={styles.inputGroup} className="input-group-1">
                <label style={styles.label}>Username</label>
                <Input
                  prefix={<UserOutlined style={{ color: adminThemeStyles.subtitleColor }} />}
                  placeholder="admin#()"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="signin-input"
                  style={styles.input}
                  disabled={isBlocked}
                />
              </div>

              {/* Password Field */}
              <div style={styles.inputGroup} className="input-group-2">
                <label style={styles.label}>Password</label>
                <Input.Password
                  prefix={<LockOutlined style={{ color: adminThemeStyles.subtitleColor }} />}
                  placeholder="Enter your secure password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="signin-input"
                  style={styles.input}
                  iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                  disabled={isBlocked}
                />
              </div>

              {/* Remember Me */}
              <div style={{ marginBottom: '24px' }}>
                <Checkbox
                  checked={formData.rememberMe}
                  onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                  style={{ color: adminThemeStyles.subtitleColor }}
                  disabled={isBlocked}
                >
                  Keep me signed in for 7 days
                </Checkbox>
              </div>

              {/* Submit Button */}
              <Button
                type="primary"
                loading={loading}
                onClick={handleLogin}
                className="signin-submit-button"
                style={styles.submitButton}
                disabled={isBlocked}
              >
                {loading ? 'Authenticating...' : 'Secure Login'}
              </Button>

              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '12px', color: adminThemeStyles.subtitleColor }}>
                  🔒 this session is encrypted and monitored
                </Text>
                {loginAttempts > 0 && !isBlocked && (
                  <Text type="warning" style={{ fontSize: '12px', display: 'block', marginTop: '8px', color: '#eab308' }}>
                    ⚠️ {3 - loginAttempts} attempts remaining
                  </Text>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;